import type { JoyHotelAccommodationListingDTO, RawJoyHotelPricingInfo, StandardHotelPricingValues } from '@shared/types/Accommodations.types';
import type { AccommodationConsoleJoyPlaceLivePricingFragment } from './AccommodationsConsole.graphql.generated';
import { PriceSource } from '@shared/constants/accommodations';
import { extractHotelRedirects } from '@shared/utils/extractHotelRedirects';
import { validateTravelOfferDiag } from '@shared/types/TravelOfferDiag.validation';
import { hasAnyCancellationPolicyFree, toOtaTransferredPricing } from '@shared/utils/accommodations/AccommodationTiles';

type MapHotelRoomPrice = (pricingInfo: RawJoyHotelPricingInfo) => StandardHotelPricingValues | null;

type ApplyLivePricingArgs = Readonly<{
  listings: ReadonlyArray<JoyHotelAccommodationListingDTO>;
  /** Live result per joyPlaceId. Absent means the id's batch has not come back, or returned nothing for it. */
  pricingByJoyPlaceId: ReadonlyMap<string, AccommodationConsoleJoyPlaceLivePricingFragment>;
  /** Ids whose batch has resolved — succeeded, failed, or returned no row for them. */
  settledJoyPlaceIds: ReadonlySet<string>;
  /** A fan-out is in flight, so unpriced tiles not yet settled should show a skeleton. */
  isSettling: boolean;
  mapHotelRoomPrice: MapHotelRoomPrice;
}>;

/**
 * Patches live vendor prices onto listings the snapshot search already rendered, keyed by joyPlaceId.
 *
 * Replaces the all-or-nothing swap the console used to do (one realtime refetch replacing the whole
 * array), so prices can stream in per batch while the list, its filters and its facet counts stay put.
 *
 * The invariant that keeps every downstream consumer honest: a listing loses a price it is displaying
 * only on an authoritative "no rate" answer. A batch that fails, or that returns no row for an id, leaves
 * that tile on its snapshot price rather than blanking it; a row that comes back with a null rate does
 * clear it, so the tile drops into the unpriced bucket instead of quoting a stale rate. Without this, `dynamicPriceMax`, the price
 * filter, the unpriced sort bucket, the facet counts and the price histogram all thrash as batches land.
 *
 * Price, redirects, `_diag` and cancellation policies are patched **together or not at all**: the
 * redirect URL encodes the provider and the rate that produced the price, so a live price beside a
 * snapshot redirect would misattribute the click and check the guest out against the wrong rate.
 * Direct-booking tiles are the one exception — they show no price and their redirect is the brand's own
 * booking page, which does not come from the pricing query at all (see below).
 *
 * Stable content — `searchScore`, `otaProvider`, `tags`, `chain`, facilities, ratings, photos — is never
 * touched, so it survives from the snapshot search that computed it.
 */
export const applyLivePricingToListings = ({
  listings,
  pricingByJoyPlaceId,
  settledJoyPlaceIds,
  isSettling,
  mapHotelRoomPrice,
}: ApplyLivePricingArgs): ReadonlyArray<JoyHotelAccommodationListingDTO> => {
  // Nothing has landed and nothing is in flight: hand back the same reference so downstream memos
  // (the Fuse index, the facet pass, the sort) do not recompute for a no-op. While the first batch is
  // in flight this must not short-circuit, or unpriced tiles miss their skeleton until it lands.
  if (!isSettling && pricingByJoyPlaceId.size === 0 && settledJoyPlaceIds.size === 0) {
    return listings;
  }

  return listings.map(listing => {
    const { joyPlaceId } = listing;
    if (!joyPlaceId) {
      return listing;
    }

    // Fail closed on the cash-fund split. `JoyPlacePricingAndBookingDetails` carries no `walletSpend`,
    // and the split is computed against the rate it is shown with — patching a live price beside a
    // snapshot-derived split would state a fund/card breakdown that does not add up. The caller is
    // expected to keep the cash-fund scenario off this path entirely; this is the structural backstop.
    if (listing.walletSpend) {
      return listing;
    }

    const live = pricingByJoyPlaceId.get(joyPlaceId);

    if (!live) {
      // Settled with no row, or its batch failed: keep the snapshot price. Strictly better than the
      // swap this replaces, where a hotel missing from the realtime response vanished from the list.
      if (settledJoyPlaceIds.has(joyPlaceId)) {
        return listing;
      }
      // Still in flight. Only skeleton a tile that has no price to show — never cover a readable one.
      // A direct-booking tile never shows a price, so a skeleton would promise one that never comes.
      if (listing.isDirectBooking || listing.pricingInfo.values || listing.pricingInfo.isLoading) {
        return listing;
      }
      return { ...listing, pricingInfo: { ...listing.pricingInfo, isLoading: true } };
    }

    // Null when the vendor returned no rate for these dates. That answer is authoritative — clear the
    // snapshot price so the tile drops into the `unpriced` bucket rather than quoting a stale rate.
    const liveValues = live.pricingInfo ? mapHotelRoomPrice(live.pricingInfo) : null;

    // A currency mismatch means the batch was built from a different currency than the search that
    // produced this tile. Rendering both in one list would mix scales in `dynamicPriceMax`, the price
    // sorts and the histogram, so drop the patch and keep the snapshot rather than quote the wrong unit.
    const existingCurrency = listing.pricingInfo.values?.totalRateCurrency;
    if (liveValues?.totalRateCurrency && existingCurrency && liveValues.totalRateCurrency !== existingCurrency) {
      return listing;
    }

    // Same contract as `transformPropertiesToListings`: a direct-booking tile must not quote our OTA
    // rate as the brand's, and is never offered as free cancellation. The live rate goes only to
    // `otaTransferredPricing`, so click telemetry logs the transferred GBV the handoff carries.
    //
    // The redirect is deliberately NOT patched. A direct-booking tile's redirect is the brand's own
    // URL, which the search response built from the place's `directBookingUrl` plus the stay dates —
    // the same dates this fan-out prices with. This batched query carries only the OTA redirect, so
    // patching it here would put the OTA redirect and price back on every brand-owned tile, not just
    // the ad slots.
    if (listing.isDirectBooking) {
      return {
        ...listing,
        otaTransferredPricing: live.pricingInfo ? toOtaTransferredPricing(live.pricingInfo.totalRate, liveValues?.nightlyBaseRate, PriceSource.LIVE) : undefined,
      };
    }

    const patchedRedirects = {
      diag: live.pricingInfo?._diag ? validateTravelOfferDiag(live.pricingInfo._diag) : null,
      redirectMechanisms: live.redirectMechanisms || undefined,
      isSoldOutForSelectedDates: !!extractHotelRedirects(live.redirectMechanisms)?.hotelSearchPageUrl,
    };

    return {
      ...listing,
      pricingInfo: {
        isLoading: false,
        values: liveValues,
        // Only a price that landed has a source; otherwise telemetry names a fetch nobody saw.
        source: liveValues ? PriceSource.LIVE : undefined,
      },
      ...patchedRedirects,
      hasFreeCancellation: hasAnyCancellationPolicyFree(live.pricingInfo?.cancellationPolicies),
    };
  });
};
